import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TControllerProps } from 'common/types';
import { AutocompleteField } from 'common/forms/Field/AutocompleteField';
import { AI_TRANSLATION_LANGUAGES } from 'common/constants/languages';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    name: string;
    label?: string;
}

export const AILanguageSelectField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
    label,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();

    const control = asTypedControl<string>(name, formControl);

    const {
        field,
        fieldState: { error },
    } = useController({ name, control });

    /** Find a language by its English value (what gets sent to the backend). */
    const findByValue = (value: string) =>
        AI_TRANSLATION_LANGUAGES.find((lang) => lang.value === value);

    const getLabel = useCallback(
        (value: string) => {
            const lang = findByValue(value);

            return lang ? translate(`languages.${lang.translationKey}`) : value;
        },
        [translate]
    );

    const options = AI_TRANSLATION_LANGUAGES.map((lang) => lang.value);

    const [inputValue, setInputValue] = useState(() =>
        getLabel(field.value || '')
    );

    // Sync inputValue when field.value changes externally (e.g. form reset)
    useEffect(() => {
        setInputValue(getLabel(field.value || ''));
    }, [field.value, getLabel]);

    return (
        <AutocompleteField
            freeSolo
            {...field}
            error={!!error}
            getOptionLabel={(option) => getLabel(option)}
            helperText={error?.message}
            InputProps={{
                label: label ?? translate('language'),
            }}
            inputValue={inputValue}
            options={options}
            renderOption={({ key, ...props }, option) => {
                const lang = findByValue(option);

                return (
                    <li key={key} {...props}>
                        {lang?.flag && (
                            <Box component="span" sx={{ mr: 1 }}>
                                {lang.flag}
                            </Box>
                        )}
                        {getLabel(option)}
                    </li>
                );
            }}
            value={field.value || null}
            onChange={(_event, newValue) => {
                const value = String(newValue || '');
                field.onChange(value);
                setInputValue(getLabel(value));
            }}
            onInputChange={(_event, newInputValue, reason) => {
                if (reason === 'input') {
                    setInputValue(newInputValue);
                    field.onChange(newInputValue);
                }
            }}
        />
    );
};
